import { type DataImport, lookup } from '@cruz-com/lookup'
import _ from 'lodash'
import moment from 'moment'
import { useCallback } from 'react'
import { CardBody, CardHeader, CardItem, CardPhoto, Link } from '@/components'
import { DateTimeFormat } from '@/settings'

export default function RawVehicleCard(d: DataImport.RawVehicle) {
  const url = `/carsforsale-import/vehicle?id=${d._id}`
  const issueCount = d?.log?.issue_count || 0

  const getPostStatus = useCallback(() => {
    if (!d?.post_status_id) {
      return '-'
    }
    return _.find(lookup.other.postStatus, { id: d.post_status_id })?.name || '-'
  }, [d?.post_status_id])

  const getSaleStatus = useCallback(() => {
    if (!d?.sale_status_id) {
      return '-'
    }
    return _.find(lookup.vehicle.saleStatus, { id: d.sale_status_id })?.name || '-'
  }, [d?.sale_status_id])

  const getLocation = useCallback(() => {
    if (!d?.location?.zip_code) {
      return '-'
    }
    return `${d?.location?.city}, ${d?.location?.state_abbr} ${d?.location?.zip_code}`
  }, [d?.location?.zip_code])

  return (
    <>
      <CardPhoto href={url} photoUrl={d?.photo_preview_url || ''} />
      <CardHeader className='bg-transparent'>
        <Link href={url} className='block w-full'>
          <span className='font-semibold'>
            {d.year} {d.make} {d.model}
          </span>
          {d?.is_dup && <span className='ml-2 text-xs text-red-700'>(DUP)</span>}
          {_.size(d?.issues) > 0 && <span className='ml-2 text-xs text-red-700'>(Data Issue)</span>}
        </Link>
      </CardHeader>
      <CardBody>
        <CardItem label='Inventory ID' value={d?.inventory_id} />
        <CardItem label='Location ID' value={d?.source_location_id} />
        <CardItem label='Location' value={getLocation()} />
        <CardItem label='Post Status' alert={(d?.post_status_id || 0) < 1} value={getPostStatus()} />
        <CardItem label='Sale Status' alert={(d?.sale_status_id || 0) < 1} value={getSaleStatus()} />
        <CardItem label='VIN' value={d?.vin || '-'} />
        <CardItem label='VIN Pattern' value={d?.vin_pattern || '-'} />
        <CardItem
          alert={d?.log?.decode_method !== 'vin'}
          label='Decode Method'
          value={d?.log?.decode_method === 'vin' ? 'VIN' : _.startCase(d?.log?.decode_method) || 'Not Processed'}
        />
        <CardItem label='DataOne Vehicle ID' value={d?.log?.dataone_vehicle_id || '-'} />
        <CardItem alert={issueCount > 0} label='Issues' value={issueCount || '-'} />
        <CardItem label='Trim' value={d?.trim || '-'} />
        <CardItem label='Style' value={d?.style || '-'} />
        <CardItem label='Transmission' value={d?.transmission || '-'} />
        <CardItem label='Engine' value={d?.engine || '-'} />
        <CardItem label='Mileage' value={d?.mileage?.toLocaleString()} />
        <CardItem label='Price' value={_.toNumber(d.price) > 0 ? `$${d?.price?.toLocaleString()}` : '-'} />
        <CardItem
          label='Created'
          value={
            <>
              {moment(d.date_create).fromNow()}
              <span className='font-light ml-1'>({moment(d.date_create).format(DateTimeFormat)})</span>
            </>
          }
        />
        <CardItem
          label='Updated'
          value={
            d.date_update ? (
              <>
                {moment(d.date_update).fromNow()}
                <span className='font-light ml-1'>({moment(d.date_update).format(DateTimeFormat)})</span>
              </>
            ) : (
              '-'
            )
          }
        />
      </CardBody>
    </>
  )
}
